import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, MapPin, User, CalendarPlus } from "lucide-react";
import { useArtisanAppointments } from "@/hooks/useArtisanAppointments";
import { generateGoogleCalendarUrl } from "@/hooks/useAppointments";
import type { Appointment } from "@/hooks/useAppointments";
import { useLanguage } from "@/hooks/useLanguage";

const STATUS_GROUPS = {
  upcoming: ["proposed", "counterProposed", "accepted"],
  archived: ["declined", "canceled", "expired"],
};

const AppointmentHistory = () => {
  const { t, locale } = useLanguage("artisan");
  const loc = locale === "ar" ? "ar-DZ" : "fr-FR";
  const [filter, setFilter] = useState("upcoming");
  const { data: allAppointments = [], isLoading } = useArtisanAppointments("all");

  const tabFilters = [
    { value: "upcoming", label: t("rdv_history.tab_upcoming") },
    { value: "archived", label: t("rdv_history.tab_archived") },
    { value: "all",      label: t("rdv_history.tab_all") },
  ];

  const statusBadge: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    proposed:        { label: t("rdv_history.status_proposed"), variant: "secondary" },
    counterProposed: { label: t("rdv_history.status_counter"),  variant: "outline" },
    accepted:        { label: t("rdv_history.status_accepted"), variant: "default" },
    declined:        { label: t("rdv_history.status_declined"), variant: "destructive" },
    canceled:        { label: t("rdv_history.status_canceled"), variant: "destructive" },
    expired:         { label: t("rdv_history.status_expired"),  variant: "secondary" },
  };

  const appointments = filter === "all"
    ? allAppointments
    : allAppointments.filter((a: any) => STATUS_GROUPS[filter as keyof typeof STATUS_GROUPS]?.includes(a.status));

  const locationLabel = (apt: Appointment) =>
    apt.location_type === "call"
      ? `📞 ${t("rdv_history.location_call")}`
      : apt.location_type === "clientAddress"
        ? `🏠 ${apt.location_text || t("rdv_history.location_client")}`
        : `📍 ${apt.location_text}`;

  const emptyMsg =
    filter === "upcoming" ? t("rdv_history.empty_upcoming")
    : filter === "archived" ? t("rdv_history.empty_archived")
    : t("rdv_history.empty_all");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          {t("rdv_history.title")}
        </h2>
        <Badge variant="secondary" className="text-xs">
          {appointments.length} RDV
        </Badge>
      </div>

      {/* Status filter chips */}
      <div className="flex gap-2">
        {tabFilters.map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant={filter === f.value ? "default" : "outline"}
            className="text-xs h-7 px-3"
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-muted-foreground text-sm">{t("rdv_history.loading")}</div>
      ) : appointments.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Calendar className="mx-auto h-10 w-10 text-muted-foreground/30" />
            <p className="mt-2 text-sm text-muted-foreground">{emptyMsg}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {appointments.map((apt: any) => {
            const config = statusBadge[apt.status] || statusBadge.proposed;
            const startDate = new Date(apt.starts_at);
            const shortId = `RDV-${apt.id.slice(0, 4).toUpperCase()}`;

            return (
              <Card key={apt.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1.5 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono text-muted-foreground">{shortId}</span>
                        <Badge variant={config.variant} className="text-[10px]">{config.label}</Badge>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm">
                        <User className="h-3.5 w-3.5 text-primary shrink-0" />
                        <span className="font-medium truncate">{apt.client_name}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {startDate.toLocaleDateString(loc, { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {startDate.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" })} · {apt.duration_minutes} min
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {locationLabel(apt)}
                        </span>
                      </div>
                      {apt.notes && (
                        <p className="text-xs italic text-muted-foreground mt-1">{apt.notes}</p>
                      )}
                    </div>

                    {apt.status === "accepted" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[10px] px-2 shrink-0"
                        onClick={() => window.open(generateGoogleCalendarUrl(apt), "_blank")}
                      >
                        <CalendarPlus className="h-3 w-3 me-1" /> Google
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AppointmentHistory;
